import Humanize from 'humanize-plus';

export type SparklineValue = number | null;

const toSparklineValue = (value: unknown): SparklineValue => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string') {
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
  }
  if (Array.isArray(value)) {
    const candidate = value.length > 1 ? value[1] : value[0];
    return toSparklineValue(candidate);
  }
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const candidate = record.value ?? record.Value ?? record.y ?? record.Y ?? record.v ?? record.V;
    if (candidate !== undefined) return toSparklineValue(candidate);
  }
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const parseDelimitedString = (raw: string): SparklineValue[] =>
  raw.split(',').map(part => toSparklineValue(part.trim()));

export const formatSparklineSeries = (values: SparklineValue[]): string =>
  values.map(value => (value === null ? '' : value)).join(',');

export const formatSparklineTooltip = (value: number): string => {
  const abs = Math.abs(value);
  if (abs === 0) return '0';
  if (abs < 0.0001) return value.toExponential(4);
  if (abs < 0.01) return value.toFixed(6).replace(/\.?0+$/, '');
  if (abs < 1) return value.toFixed(4).replace(/\.?0+$/, '');
  return Humanize.toFixed(value, 2).replace('.00', '');
};

export const parseSparklineValues = (raw: unknown): SparklineValue[] => {
  if (raw === null || raw === undefined || raw === '') return [];

  if (Array.isArray(raw)) {
    return raw.map(toSparklineValue);
  }

  if (typeof raw === 'number') {
    return Number.isFinite(raw) ? [raw] : [];
  }

  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return [];
    if (trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed.map(toSparklineValue);
        }
      } catch {
        // Fall through to comma-separated parsing.
      }
    }
    return parseDelimitedString(trimmed);
  }

  return [];
};

export const pickLongestSparklineSeries = (...candidates: unknown[]): SparklineValue[] => {
  let longest: SparklineValue[] = [];

  candidates.forEach(candidate => {
    if (candidate === null || candidate === undefined || candidate === '') return;
    const parsed = parseSparklineValues(candidate);
    if (parsed.length > longest.length) {
      longest = parsed;
    }
  });

  return longest;
};

export type SparklineCell = {
  displayValue: unknown;
  series: SparklineValue[] | null;
};

export const extractSparklineCell = (cell: unknown): SparklineCell => {
  if (cell === null || cell === undefined) {
    return { displayValue: '', series: null };
  }

  if (Array.isArray(cell)) {
    return { displayValue: cell, series: cell.map(toSparklineValue) };
  }

  if (typeof cell === 'object') {
    const record = cell as Record<string, unknown>;
    const seriesCandidate =
      record.components ??
      record.Components ??
      record.data ??
      record.Data ??
      record.series ??
      record.Series ??
      record.values ??
      record.Values;

    const displayCandidate =
      record.value ?? record.Value ?? record.text ?? record.Text ?? record.summary ?? record.Summary;

    if (seriesCandidate !== undefined) {
      return {
        displayValue: displayCandidate ?? '',
        series: parseSparklineValues(seriesCandidate),
      };
    }
  }

  return { displayValue: cell, series: null };
};

export const resolveSparklineSeries = (cell: unknown): SparklineValue[] | null => {
  const extracted = extractSparklineCell(cell);
  if (extracted.series?.length) {
    return extracted.series;
  }

  if (Array.isArray(cell)) {
    const mapped = cell.map(toSparklineValue);
    return mapped.length ? mapped : null;
  }

  if (typeof cell === 'string') {
    const parsed = parseSparklineValues(cell);
    return parsed.length ? parsed : null;
  }

  return null;
};
